<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />

    <!-- Content Section -->
    <div class="content-section">
      <!-- Vehicles Table -->
      <div class="wizard-table-container">
        <!-- Table Header -->
        <div class="wizard-table-header">
          <div>{{ lang.W00038 }}</div>
          <div>{{ lang.W00039 }}</div>
          <div>{{ lang.W00040 }}</div>
          <div>{{ lang.W00041 }}</div>
        </div>

        <!-- Table Rows -->
        <div class="wizard-table-body">
          <div
            :key="model.CarModelGuid"
            v-for="model in filteredCarModelList"
            class="wizard-table-row"
            :class="{'invalid-wizard-table-row': multipleModels.has(model.CarModelName)}"
          >
            <div class="wizard-table-cell">{{ model.CarModelName }}</div>
            <div>
              <template v-for="importer in importerList">
                <div
                  v-if="importer.ImporterGuid === model.ImporterGuid"
                  :key="importer.ImporterGuid"
                >
                  {{ importer.ImporterName }}
                </div>
              </template>
            </div>
            <div>
              <span
                v-for="(modelCarSegment, index) in model.CarSegments"
                :key="modelCarSegment.CarSegmentGuid"
              >
                <template v-for="segment in segmentList">
                  <span
                    v-if="
                      segment.CarSegmentGuid ===
                      modelCarSegment.CarSegmentGuid
                    "
                    :key="segment.CarSegmentGuid"
                  >
                    <span v-if="index != 0"> ,</span>
                    {{ segment.CarSegmentName }}
                  </span>
                </template>
              </span>
            </div>
            <div class="form-group nopadding">
              <input
                type="number"
                :min="0"
                :max="100"
                :step="0.1"
                class="wizard-input"
                v-model.number="model.Distribution"
                @change="validateDistributionValue(model)"
                :disabled="readonly"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="sum-display">
        <span>{{ lang.W00071 }} {{ sumDistribution }}%</span>
      </div>

    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from "./StepHeader.vue";
import wizardService from "../../../services/WizardService";
export default {
  name: "Step3",
  components: {
    StepHeader,
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      localFormData: structuredClone(this.formData),
      carModelList: [],
      importerList: null,
      segmentList: null,
      multipleModels: new Set(),
      lang: {
        W00038: "Model",
        W00039: "Importer",
        W00040: "Segment",
        W00041: "Share (%)",

        W00071: "Sum share:",

        W00095: "Please insert model shares.",
        W00096: "The sum has to be 100%.",
        W00097: "Share for a model is set more than once!"
      }
    };
  },
  computed: {
    sumDistribution() {
      if(!this.localFormData.carModelDistribution || this.localFormData.carModelDistribution.length <= 0) return 0;

      let sum = 0;
      for (const element of this.localFormData.carModelDistribution) {
        if(element.Distribution) sum += Number(element.Distribution)
      }
      return Number((sum * 100).toFixed(1));
    },
    filteredCarModelList() {
      if (!this.carModelList || !Array.isArray(this.carModelList)) return [];
      const importerGuid = this.formData && this.formData.importerGuid;
      return this.carModelList.filter(m => {
        // include models without an ImporterGuid or that match the selected importer
        return !m.ImporterGuid || m.ImporterGuid === importerGuid;
      });
    }
  },
  watch: {
    "formData": {
      deep: true,
      handler() {
        this.localFormData = structuredClone(this.formData);
      }
    },
    "localFormData": {
      deep: true,
      handler(newVal) {
        this.$emit('update:formData', newVal);
      }
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
  },
  mounted() {
    if (this.globals.selectedLanguage) { 
      this.globals.getLanguageStrings(this);
    }
    this.clearCarModelDistribution();
    this.readCarModel();
    this.readImporters();
    this.readCarSegment();
  },
  methods: {
    validateStep() {
      this.multipleModels.clear();
      this.$forceUpdate();
      
      if(!this.localFormData.carModelDistribution || this.localFormData.carModelDistribution.length <= 0) return { valid: false, msg: this.lang.W00095};

      let modelsSet = new Set();
      for (let entry of this.carModelList) {
        if(!entry.Distribution) continue;
        if(modelsSet.has(entry.CarModelName)) {
          this.multipleModels.add(entry.CarModelName);
          this.$forceUpdate();
          return { valid: false, msg: this.lang.W00097};
        }
        modelsSet.add(entry.CarModelName);
      }
      this.multipleModels.clear();

      if(Number(this.sumDistribution) != 100) return { valid: false, msg: this.lang.W00096};

      return { valid: true, msg: null};
    },
    clearCarModelDistribution() {
      for (let entry of this.carModelList) delete entry.Distribution;
    },
    readCarModelDistribution() {
      if (!this.carModelList) return;

      for (let waveEntry of this.carModelList) {
        let modelListEntry = _.find(
          this.carModelList,
          (model) => model.CarModelGuid === waveEntry.CarModelGuid
        );
        if (modelListEntry)
          modelListEntry.Distribution = parseFloat(
            (waveEntry.Distribution * 100).toFixed(4)
          );
      }
    },
    async readCarModel() {
      if(!this.localFormData.carModelDistribution) this.$set(this.localFormData, 'carModelDistribution', []); 

      let res = await wizardService.readCarModel();
      if (res.length && res.length > 0) {
        this.carModelList = res;
      }
      const map = this.localFormData.carModelDistribution.reduce((acc, item) => {
        acc[item.CarModelGuid] = item.Distribution;
        return acc;
      }, {});

      this.carModelList = this.carModelList.map((model) => ({
        ...model,
        Distribution: map[model.CarModelGuid] || null,
      }));
      this.readCarModelDistribution();
    },
    async readImporters() {
      let res = await wizardService.readImporters();
      if (res.length && res.length > 0) this.importerList = res;
    },
    async readCarSegment() {
      let res = await wizardService.readCarSegment();
      if (res.length && res.length > 0) this.segmentList = res;
    },
    validateDistributionValue(model) {
      let lowerLimit = 0,
        upperLimit = 100;

      if (typeof model.Distribution !== "number" || model.Distribution < lowerLimit) {
        model.Distribution = lowerLimit;
      } else if (model.Distribution > upperLimit) {
        model.Distribution = upperLimit;
      }

      let formDataCarModel = this.localFormData.carModelDistribution.find(e => e.CarModelGuid === model.CarModelGuid);

      if (formDataCarModel) {
        if(!model.Distribution) {
          this.localFormData.carModelDistribution = this.localFormData.carModelDistribution.filter(e => e != formDataCarModel);
        } else {
          formDataCarModel.Distribution = parseFloat((model.Distribution / 100).toFixed(4));          
        }
      } else if(model.Distribution) {
        this.localFormData.carModelDistribution.push({
          CarModelGuid: model.CarModelGuid,
          Distribution: parseFloat((model.Distribution / 100).toFixed(4)),
        });
      }
      this.$forceUpdate();
    }   
  },
};
</script>

<style lang="css" scoped>
.vehicles-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1280px) {
    grid-template-columns: 2fr 1fr;
    gap: 48px;
  }
}

.wizard-table-header {
  grid-template-columns: 2fr 1fr 1fr 1fr;
}

.wizard-table-row {
  grid-template-columns: 2fr 1fr 1fr 1fr;
}

.sum-display {
  padding: 0px 50px;
  font-weight: bold;
  font-size: 14pt;
  text-align: end;
}
</style>



// WEBPACK FOOTER //
// Step3.vue?602986c4